import {
  ContextAwarenessPolicy,
  type EnterpriseSettings,
  LocalDataPolicy,
  MdmProvider,
  MdmUpdateIntent,
  NoteSharingAudience,
  NoteSharingContent,
  NotetakerTranscriptRetention,
  OrgInsightsMode,
  SettingType,
  SnippetSharingPolicy,
} from '@/types'

// Define default settings
export const DEFAULT_SETTINGS: EnterpriseSettings = {
  [SettingType.zdr]: true,
  [SettingType.allowShareData]: false,
  [SettingType.allowCloudSync]: true,
  [SettingType.localDataDelete]: LocalDataPolicy.StoreNormally,
  [SettingType.enforceSSO]: false,
  // TODO: Turn this back on once we figure out what to do on the backend
  [SettingType.autoInvite]: false,
  [SettingType.disableTeamTrial]: false,
  [SettingType.contextAwareness]: ContextAwarenessPolicy.Available,
  [SettingType.hideLeaderboard]: false,
  [SettingType.enforceMuteAudio]: false,
  [SettingType.allowNoteSharing]: true,
  [SettingType.allowScratchpad]: true,
  [SettingType.allowDictionary]: true,
  [SettingType.allowSnippets]: true,
  [SettingType.notetakerTranscriptRetention]: NotetakerTranscriptRetention.ManagedIndividually,
  [SettingType.notetakerConsentConfirmation]: false,
  [SettingType.notetakerConsentChatEnabled]: false,
  [SettingType.snippetSharingPolicy]: SnippetSharingPolicy.Unrestricted,
  [SettingType.mcpEnabled]: true,
  [SettingType.allowTransforms]: true,
  [SettingType.connectorGoogleCalendarEnabled]: true,
  [SettingType.connectorGmailEnabled]: true,
  [SettingType.connectorSlackEnabled]: true,
  [SettingType.connectorNotionEnabled]: true,
  [SettingType.connectorOutlookEnabled]: true,
  [SettingType.disableMobileSignIn]: false,
}

const NOTE_SHARING_AUDIENCES: readonly NoteSharingAudience[] = [
  NoteSharingAudience.Owner,
  NoteSharingAudience.Internal,
  NoteSharingAudience.External,
  NoteSharingAudience.Public,
]

/**
 * Mirror of the backend's `resolve_note_sharing_audience`, including its fail-open direction.
 *
 * Relies on the audience key having no `DEFAULT_SETTINGS` entry: one would satisfy the first
 * branch for every org predating the key and strand them on the default.
 */
export function resolveNoteSharingAudience(settings: EnterpriseSettings | undefined): NoteSharingAudience {
  const raw = settings?.[SettingType.noteSharingAudience]
  if (raw !== undefined && NOTE_SHARING_AUDIENCES.includes(raw)) return raw
  // The legacy bool's `false` only ever blocked public links, never sharing outright.
  if (settings?.[SettingType.allowNoteSharing] === false) return NoteSharingAudience.Internal
  return NoteSharingAudience.Public
}

const NOTE_SHARING_CONTENTS: readonly NoteSharingContent[] = [
  NoteSharingContent.NotesOnly,
  NoteSharingContent.NotesAndTranscript,
]

/**
 * Mirror of the backend's `resolve_note_sharing_content`, including its fail-open direction.
 *
 * Relies on the content key having no `DEFAULT_SETTINGS` entry, same as the audience above.
 */
export function resolveNoteSharingContent(settings: EnterpriseSettings | undefined): NoteSharingContent {
  const raw = settings?.[SettingType.noteSharingContent]
  if (raw !== undefined && NOTE_SHARING_CONTENTS.includes(raw)) return raw
  return NoteSharingContent.NotesAndTranscript
}

const ORG_INSIGHTS_MODES: readonly OrgInsightsMode[] = Object.values(OrgInsightsMode)

/** An org that never chose, or holds a value this build doesn't know, reads as the backend default. */
export function resolveOrgInsightsMode(settings: EnterpriseSettings | undefined): OrgInsightsMode {
  const raw = settings?.[SettingType.orgInsightsMode]
  if (raw !== undefined && ORG_INSIGHTS_MODES.includes(raw)) return raw
  return OrgInsightsMode.Individual
}

const MDM_PROVIDERS: readonly string[] = Object.values(MdmProvider)
const MDM_UPDATE_INTENTS: readonly string[] = Object.values(MdmUpdateIntent)

/** The providers an admin declared; values this build does not know are dropped. */
export function resolveMdmProviders(settings: EnterpriseSettings | undefined): MdmProvider[] {
  const raw = settings?.[SettingType.mdmProviders]
  return Array.isArray(raw) ? raw.filter((provider) => MDM_PROVIDERS.includes(provider)) : []
}

/** The declared update plan, or null while the admin has not completed setup. */
export function resolveMdmUpdateIntent(settings: EnterpriseSettings | undefined): MdmUpdateIntent | null {
  const raw = settings?.[SettingType.mdmUpdateIntent]
  return raw !== undefined && MDM_UPDATE_INTENTS.includes(raw) ? raw : null
}

const LOCAL_DATA_POLICIES: readonly unknown[] = Object.values(LocalDataPolicy)

/** Legacy orgs can hold a boolean here; anything outside the enum reads as Store, matching the backend. */
export function normalizeLocalDataPolicy(value: unknown): LocalDataPolicy {
  return LOCAL_DATA_POLICIES.includes(value) ? (value as LocalDataPolicy) : LocalDataPolicy.StoreNormally
}

// Boolean settings that use toggle switches
export type BooleanSettingType = Exclude<
  SettingType,
  | SettingType.localDataDelete
  | SettingType.contextAwareness
  | SettingType.allowNoteSharing
  | SettingType.allowNotetaker
  | SettingType.allowScratchpad
  | SettingType.allowDictionary
  | SettingType.allowSnippets
  | SettingType.notetakerTranscriptRetention
  | SettingType.snippetSharingPolicy
  | SettingType.notetakerConsentChatMessage
  | SettingType.noteSharingAudience
  | SettingType.noteSharingContent
  | SettingType.orgInsightsMode
  | SettingType.mdmProviders
  | SettingType.mdmUpdateIntent
>

export type SettingConfig<T extends SettingType = SettingType> = {
  label: string
  key: T
  enterpriseOnly: boolean
  requiresSubscription?: boolean
  // The backend lets any admin switch this off without the plan, so a downgraded org can lift a restriction it can't
  // re-enable.
  offNeedsNoPlan?: boolean
}

export type BooleanSettingConfig = SettingConfig<BooleanSettingType>

// Settings managed outside the main settings list (rendered by dedicated UI components
// or controlled by other flows). Excluded from `SETTINGS_CONFIG`.
type ManagedSettingType =
  | SettingType.zdrLocked
  | SettingType.localDataDelete
  | SettingType.usageExportAllowed
  | SettingType.showLastUsedAt
  | SettingType.restrictDomainAccess
  | SettingType.contextAwareness
  | SettingType.ipAllowlist
  | SettingType.ipAllowlistLocked
  | SettingType.ipAllowlistUiVisible
  | SettingType.granularDataControlsUiVisible
  | SettingType.allowShareData
  | SettingType.allowCloudSync
  | SettingType.appDenyList
  | SettingType.appDenyListUiVisible
  | SettingType.browserUrlDenyList
  | SettingType.notetakerBlockedDomains
  | SettingType.notetakerBlockedDomainsUiVisible
  | SettingType.allowNoteSharing
  | SettingType.allowNotetaker
  | SettingType.allowScratchpad
  | SettingType.allowDictionary
  | SettingType.allowSnippets
  | SettingType.notetakerTranscriptRetention
  | SettingType.snippetSharingPolicy
  | SettingType.mcpEnabled
  | SettingType.connectorGoogleCalendarEnabled
  | SettingType.connectorGmailEnabled
  | SettingType.connectorSlackEnabled
  | SettingType.connectorNotionEnabled
  | SettingType.connectorOutlookEnabled
  | SettingType.auditLogStreamingUiVisible
  | SettingType.dictionarySnippetsKillSwitchUiVisible
  | SettingType.notetakerConsentChatMessage
  | SettingType.noteSharingAudience
  | SettingType.noteSharingContent
  | SettingType.orgInsightsMode
  | SettingType.mdmProviders
  | SettingType.mdmUpdateIntent
  | SettingType.defaultSeatType

// Define settings configuration for better maintainability
export const SETTINGS_CONFIG = {
  zdr: {
    label: 'Enforce zero data retention',
    key: SettingType.zdr,
    enterpriseOnly: true,
  },
  enforceSSO: {
    label: 'Enforce SSO for all members',
    key: SettingType.enforceSSO,
    enterpriseOnly: true,
  },
  autoInvite: {
    label: 'Automatically add new users with a company email to the enterprise plan',
    key: SettingType.autoInvite,
    enterpriseOnly: false,
  },
  disableTeamTrial: {
    label: 'Disable free trial for new members joining this organization',
    key: SettingType.disableTeamTrial,
    enterpriseOnly: false,
    requiresSubscription: true,
  },
  hideLeaderboard: {
    label: 'Hide leaderboard',
    key: SettingType.hideLeaderboard,
    enterpriseOnly: false,
  },
  enforceMuteAudio: {
    label: 'Always mute audio while dictating',
    key: SettingType.enforceMuteAudio,
    enterpriseOnly: false,
  },
  allowTransforms: {
    label: 'Transforms',
    key: SettingType.allowTransforms,
    enterpriseOnly: false,
  },
  notetakerConsentConfirmation: {
    label: 'Require consent confirmation',
    key: SettingType.notetakerConsentConfirmation,
    enterpriseOnly: true,
  },
  notetakerConsentChatEnabled: {
    label: 'Chat disclosure message',
    key: SettingType.notetakerConsentChatEnabled,
    enterpriseOnly: true,
  },
  disableMobileSignIn: {
    label: 'Block mobile app sign-in',
    key: SettingType.disableMobileSignIn,
    enterpriseOnly: true,
    requiresSubscription: true,
    offNeedsNoPlan: true,
  },
} as const satisfies Record<Exclude<SettingType, ManagedSettingType>, SettingConfig>
